import { useTranslation } from 'react-i18next';
/**
 * SignatoriesList — manage client + internal stamp signatories.
 * Co-locates SignatoryEditor since it's only used here.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Plus, Pencil, Trash2, FileSignature } from 'lucide-react';
import { API, SignatoryStampPreview } from './_shared';
import SignaturePad from '../ui/SignaturePad';

// Convert a data-URL (e.g. from SignaturePad) into a Blob so it can
// be uploaded via multipart/form-data alongside the existing file-
// picker flow. Keeps the backend contract unchanged.
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:([^;]+)/)?.[1] || 'image/png';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function SignatoryEditor({ signatory, customers, onClose, onSaved }) {
  const { t } = useTranslation();
  const isNew = !signatory;
  const [s, setS] = useState(signatory || {
    customer_id: '', name: '', position: '', role_label: '', email: '', phone: '',
  });
  const [file, setFile] = useState(null);
  // Alternative to `file`: the operator can draw the signature
  // directly inside the dialog. If both are set, the uploaded file
  // wins (explicit choice via the file picker).
  const [drawnUrl, setDrawnUrl] = useState(null);

  const save = async () => {
    if (!s.name) { toast.error(t('auto.ctr.nameIsRequired')); return; }
    try {
      // Empty string customer_id → null on the backend = internal stamp
      const payload = { ...s, customer_id: s.customer_id || null };
      let saved;
      if (isNew) {
        saved = (await axios.post(`${API}/client-stamp-signatories`, payload)).data;
      } else {
        saved = (await axios.put(`${API}/client-stamp-signatories/${s.id}`, payload)).data;
      }
      // Prefer the uploaded PNG; fall back to the drawn signature.
      let uploadBlob = file;
      let uploadName = file?.name;
      if (!uploadBlob && drawnUrl) {
        uploadBlob = dataUrlToBlob(drawnUrl);
        uploadName = 'drawn-signature.png';
      }
      if (uploadBlob) {
        const fd = new FormData();
        fd.append('file', uploadBlob, uploadName || 'signature.png');
        await axios.post(`${API}/client-stamp-signatories/${saved.id}/stamp`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(t('auto.ctr.saved')); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="signatory-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New signatory' : `Edit · ${s.name}`}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pick <strong>{t('auto.ctr.expertfixInternal')}</strong> for our own support letters,
            or a customer for a stamp issued by that customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('auto.ctr.issuedBy')}</Label>
            <Select
              value={s.customer_id || '__internal__'}
              onValueChange={(v) => setS(prev => ({ ...prev, customer_id: v === '__internal__' ? '' : v  }))}
            >
              <SelectTrigger data-testid="signatory-customer"><SelectValue placeholder={t('auto.ctr.pick')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__internal__">{t('auto.ctr.expertfixInternal')}</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('auto.ctr.fullName')}</Label>
              <Input value={s.name} onChange={(e) => setS(prev => ({ ...prev, name: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.position')}</Label>
              <Input value={s.position || ''} onChange={(e) => setS(prev => ({ ...prev, position: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.roleLabelOptional')}</Label>
              <Input value={s.role_label || ''} placeholder="e.g. Project Manager"
                onChange={(e) => setS(prev => ({ ...prev, role_label: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.ctr.email')}</Label>
              <Input type="email" value={s.email || ''}
                onChange={(e) => setS(prev => ({ ...prev, email: e.target.value  }))} />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('auto.ctr.phone')}</Label>
              <Input value={s.phone || ''} onChange={(e) => setS(prev => ({ ...prev, phone: e.target.value  }))} />
            </div>
          </div>
          <div>
            <Label>{t('auto.ctr.stampSignatureImagePng5Mb')}</Label>
            <Input type="file" accept="image/png,image/jpeg"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {!file && s.signature_image_grid_id && (
              <SignatoryStampPreview
                signatoryId={s.id}
                className="mt-2 h-16 max-w-[200px] object-contain border bg-slate-50"
              />
            )}
            {!file && (
              <div className="mt-3">
                <Label className="text-xs text-slate-500">{t('auto.ctr.orDrawTheSignature')}</Label>
                <SignaturePad
                  value={drawnUrl}
                  onChange={setDrawnUrl}
                  height={110}
                  testIdPrefix="signatory-draw"
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
          <Button onClick={save} data-testid="signatory-save">{t('auto.ctr.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SignatoriesList() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [fromEmployeeOpen, setFromEmployeeOpen] = useState(false);

  const reload = async () => {
    const [s, c, e] = await Promise.all([
      axios.get(`${API}/client-stamp-signatories`),
      axios.get(`${API}/customers`),
      axios.get(`${API}/employees`).catch(() => ({ data: [] })),
    ]);
    setItems(s.data || []);
    setCustomers(c.data || []);
    setEmployees(e.data || []);
  };
  useEffect(() => { reload(); }, []);

  const customerName = (cid) => customers.find((c) => c.id === cid)?.name || '—';

  const remove = async (sid) => {
    if (!window.confirm(t('auto.ctr.archiveThisSignatoryTheirStampWill'))) return;
    await axios.delete(`${API}/client-stamp-signatories/${sid}`);
    toast.success(t('auto.ctr.archived')); reload();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" /> Client stamps &amp; signatories
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFromEmployeeOpen(true)}
              data-testid="signatory-from-employee-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> {t('auto.ctr.addFromEmployee')}
            </Button>
            <Button size="sm" onClick={() => setCreating(true)} data-testid="signatory-new-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('auto.ctr.newSignatory')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            {t('auto.ctr.noSignatoriesYetAddThePerson')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.customer')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.name')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.position')}</th>
                <th className="text-left px-2 py-1.5">{t('auto.ctr.email')}</th>
                <th className="text-left px-2 py-1.5 w-20">{t('auto.ctr.stamp')}</th>
                <th className="text-right px-2 py-1.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t" data-testid={`signatory-row-${s.id}`}>
                  <td className="px-2 py-2">
                    {s.customer_id ? customerName(s.customer_id) : (
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                        {t('auto.ctr.expertfixInternal')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-2 py-2 font-medium">{s.name}</td>
                  <td className="px-2 py-2">{s.position || '—'}</td>
                  <td className="px-2 py-2">{s.email || '—'}</td>
                  <td className="px-2 py-2">
                    {s.signature_image_grid_id ? (
                      <SignatoryStampPreview
                        signatoryId={s.id}
                        className="h-8 max-w-[80px] object-contain border"
                      />
                    ) : <span className="text-[10px] text-slate-400 italic">none</span>}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(s.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {(editing || creating) && (
        <SignatoryEditor
          signatory={editing}
          customers={customers}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); reload(); }}
        />
      )}
      {fromEmployeeOpen && (
        <FromEmployeeDialog
          employees={employees}
          existingSignatories={items}
          onClose={() => setFromEmployeeOpen(false)}
          onSaved={() => { setFromEmployeeOpen(false); reload(); }}
        />
      )}
    </Card>
  );
}

/**
 * FromEmployeeDialog — one-click flow to create an internal signatory
 * from an existing employee record. The backend copies name / position
 * / email / phone and, if the linked user has a saved default signature
 * in profile, uploads it as the stamp so the row is signable on save.
 */
function FromEmployeeDialog({ employees, existingSignatories, onClose, onSaved }) {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [saving, setSaving] = useState(false);
  // Map of {employee_id: default_signature_data_uri} for every
  // employee whose linked user has saved a default signature.
  // Employees not in this map fall through to a "no signature
  // saved" hint next to their name.
  const [signatureMap, setSignatureMap] = useState({});
  const [loadingSignatures, setLoadingSignatures] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/client-stamp-signatories/employee-signatures`);
        if (!cancelled) setSignatureMap(data || {});
      } catch {
        if (!cancelled) setSignatureMap({});
      } finally {
        if (!cancelled) setLoadingSignatures(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter out employees who already have an active internal signatory,
  // matching the 409 the backend enforces so we surface it before the
  // round-trip.
  const takenEmployeeIds = new Set(
    (existingSignatories || [])
      .filter((s) => !s.customer_id && s.is_active !== false && s.source_employee_id)
      .map((s) => s.source_employee_id),
  );
  const availableEmployees = (employees || []).filter(
    (e) => !takenEmployeeIds.has(e.id),
  );

  const selectedSignature = employeeId ? signatureMap[employeeId] : null;
  const selectedEmployee = availableEmployees.find((e) => e.id === employeeId);

  const create = async () => {
    if (!employeeId) { toast.error(t('auto.ctr.pickAnEmployee')); return; }
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API}/client-stamp-signatories/from-employee`,
        { employee_id: employeeId, role_label: roleLabel || undefined },
      );
      if (data.signature_image_grid_id) {
        toast.success(`Added ${data.name} with their saved signature`);
      } else {
        toast.success(`Added ${data.name} — upload a stamp when ready`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add signatory');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="from-employee-dialog">
        <DialogHeader>
          <DialogTitle>{t('auto.ctr.addSignatoryFromEmployee')}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Copies the employee's name, position, email and phone. If they
            saved a default signature under <strong>{t('auto.ctr.myProfileMySignature')}</strong>,
            it becomes their stamp automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('auto.ctr.employee')}</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="from-employee-picker">
                <SelectValue placeholder={availableEmployees.length ? t('auto.ctr.pickAnEmployee2') : t('auto.ctr.noAvailableEmployees')} />
              </SelectTrigger>
              <SelectContent>
                {availableEmployees.length === 0 && (
                  <SelectItem value="__none__" disabled>{t('auto.ctr.everyEmployeeAlreadyHasASignatory')}</SelectItem>
                )}
                {availableEmployees.map((e) => {
                  const hasSig = !!signatureMap[e.id];
                  return (
                    <SelectItem key={e.id} value={e.id}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className={`inline-block h-2 w-2 rounded-full ${hasSig ? 'bg-emerald-500' : 'bg-slate-300'}`}
                          title={hasSig ? t('auto.ctr.signatureSaved') : t('auto.ctr.noSignatureSaved')}
                        />
                        <span>{e.name}{e.position_1 ? ` · ${e.position_1}` : ''}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedEmployee && (
              <div
                className="mt-2 rounded border border-slate-200 bg-slate-50 p-3"
                data-testid="from-employee-signature-preview"
              >
                {loadingSignatures ? (
                  <div className="text-xs italic text-slate-400">{t('auto.ctr.checkingSavedSignatures')}</div>
                ) : selectedSignature ? (
                  <div className="space-y-1">
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-medium">
                      {t('auto.ctr.savedSignatureWillBeUsed')}
                    </div>
                    <img
                      src={selectedSignature}
                      alt={`${selectedEmployee.name} signature`}
                      className="h-16 max-w-full object-contain bg-white border border-slate-200 rounded"
                      data-testid="from-employee-signature-image"
                    />
                  </div>
                ) : (
                  <div className="text-xs text-slate-500">
                    <strong>{t('auto.ctr.noSignatureSaved2')}</strong> The signatory row will
                    be created without a stamp — {selectedEmployee.name} (or
                    an admin) can add one later under
                    <em> {t('auto.ctr.myProfileMySignature')}</em> or via the
                    signatory's pencil icon.
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>{t('auto.ctr.roleLabelOptional')}</Label>
            <Input
              placeholder="e.g. Project Manager"
              value={roleLabel}
              onChange={(e) => setRoleLabel(e.target.value)}
              data-testid="from-employee-role-label"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="from-employee-cancel">{t('auto.ctr.cancel')}</Button>
          <Button
            onClick={create}
            disabled={saving || !employeeId}
            data-testid="from-employee-save"
          >
            {saving ? t('auto.ctr.adding') : t('auto.ctr.addSignatory')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
